import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Comparison from 'components/Footer/FooterSections/Comparison';
import HireExperts from 'components/Footer/FooterSections/HireExperts';
import FindWork from 'components/Footer/FooterSections/FindWork';
import ForFreelancers from 'components/Footer/FooterSections/ForFreelancers';
import ForClients from 'components/Footer/FooterSections/ForClients';
import LocationJobs from 'components/Footer/FooterSections/LocationJobs';
import Resources from 'components/Footer/FooterSections/Resources';
import TwineNetwork from 'components/Footer/FooterSections/TwineNetwork';
import FooterBottom from 'components/Footer/FooterSections/FooterBottom';
import { getRoleCategoryFooterHeadingLink } from 'helpers/Constants';

import styles from './Footer.module.scss';

const Group = ({ title, href, open, children }) => (
  <div className={styles.group}>
    {href ? (
      <a className={styles.groupTitle} href={href}>
        {title}
      </a>
    ) : (
      <span className={styles.groupTitle}>{title}</span>
    )}
    {/* Bodies stay beside details — browsers hide content inside closed details. */}
    <details className={styles.disclosure} open={open}>
      <summary className={styles.summary}>
        {title}
        <span className={styles.chevron} aria-hidden="true" />
      </summary>
    </details>
    <div className={styles.groupBody}>{children}</div>
  </div>
);

Group.propTypes = {
  title: PropTypes.string.isRequired,
  href: PropTypes.string,
  open: PropTypes.bool,
  children: PropTypes.node,
};

const DynamicFooter = ({
  getSkillsSection,
  inverse = false,
  type = 'client',
}) => {
  const [skillsSectionCategory, setSkillsSectionCategory] = useState(null);
  const selectedRoleCategoryState = useSelector(
    (state) => state.selectedRoleCategory
  );
  const { category } = selectedRoleCategoryState;

  useEffect(() => {
    setSkillsSectionCategory(category);
  }, [category]);

  const isClient = type === 'client';
  const skillsHref = isClient
    ? getRoleCategoryFooterHeadingLink(skillsSectionCategory)
    : '/jobs';
  const skillsTitle = isClient ? 'In demand roles' : 'In demand skills';

  const groups = [
    isClient
      ? {
          title: 'Hire experts',
          href: '/hire-a-freelancer',
          body: <HireExperts omitHeading />,
        }
      : {
          title: 'Find work',
          href: '/jobs',
          body: <FindWork omitHeading />,
        },
    {
      title: skillsTitle,
      href: skillsHref,
      body: (
        <ul className={styles.linkList}>
          {getSkillsSection(type, skillsSectionCategory)}
          {isClient && (
            <li>
              <a href="/hire-a-freelancer">See more skills</a>
            </li>
          )}
        </ul>
      ),
    },
    isClient
      ? {
          title: 'For freelancers',
          href: '/freelancers',
          body: <ForFreelancers omitHeading />,
        }
      : {
          title: 'For clients',
          href: '/hire-a-freelancer',
          body: <ForClients omitHeading />,
        },
    isClient
      ? {
          title: 'Comparison',
          href: '/freelancers',
          body: <Comparison omitHeading />,
        }
      : {
          title: 'Find jobs by location',
          href: '/jobs',
          body: <LocationJobs omitHeading />,
        },
    {
      title: 'Resources',
      body: <Resources isClient={isClient} omitHeading />,
    },
    {
      title: 'Twine network',
      href: 'https://www.twine.net/',
      body: <TwineNetwork omitHeading />,
    },
  ];

  return (
    <footer
      className={`${inverse ? styles.inverse : ''} ${styles.container} ${
        styles.largeFooter
      }`}
    >
      <nav
        className={`${styles.container} ${styles.largeFooterNav}`}
        aria-label="Footer"
      >
        <div className={styles.band}>
          <div className={styles.groups}>
            {groups.map((group, index) => (
              <Group
                key={group.title}
                title={group.title}
                href={group.href}
                open={index === 0}
              >
                {group.body}
              </Group>
            ))}
          </div>
          <FooterBottom />
        </div>
      </nav>
    </footer>
  );
};

DynamicFooter.propTypes = {
  getSkillsSection: PropTypes.func,
  inverse: PropTypes.bool,
  type: PropTypes.oneOf(['client', 'freelancer']),
};

export default memo(DynamicFooter);
